// pie-chart.angular20.ts
import * as d3 from 'd3';
import { ElementRef } from '@angular/core';

import { UserSettings, I18nService } from '@lp-ui-lib';
import { ValueRange } from './value-range';
import { Visualization } from './visualization.interface';

// ---------- Raw input ----------
export interface PieRawPoint {
  timestamp: Date | string;
  value: number;
  measuredValue: string;
}

// ---------- Internal types ----------
interface Point {
  date: Date;
  value: number;
  text: string;
}
type CategoryKey = 'Working' | 'Blow through' | 'Blocked';
interface CategoryDatum {
  key: CategoryKey;
  value: number;
}

export class PieChart implements Visualization<PieRawPoint, UserSettings> {
  // Domains & settings (kept for parity with legacy)
  private x_domain: [Date, Date] | null = null;
  private y_domain: [number, number] | null = null; // not used by pie
  private valueRanges: ValueRange[] = [];
  private y_unit = '';
  private customRange = false;

  // Data
  private data: Point[] = [];

  // D3 state
  public updateChart: (() => void) | undefined;

  constructor(
    private chartContainer: ElementRef<HTMLElement>,
    private id: string,
    private i18n: I18nService
  ) {}

  // ---------------- Visualization API ----------------
  public render(data: readonly PieRawPoint[], isMobile: boolean, settings: UserSettings): void {
    const incoming = data ?? [];
    this.data = incoming.map((t) => ({
      date: new Date(t.timestamp),
      value: t.value,
      text: t.measuredValue,
    }));
    this.renderGraph(isMobile, settings);
  }

  public SetValueUnit(unit: string): void {
    this.y_unit = unit ?? '';
  }

  public SetXDomain(start: Date, end: Date, custom = false): void {
    // Keep legacy: pie is not axis-driven, so don't snap to midnight here
    this.x_domain = d3.extent([start, end], (d) => d) as [Date, Date];
    this.customRange = custom;
    this.updateChart?.();
  }

  public ResetXDomain(): void {
    this.x_domain = null;
  }

  public SetYDomain(domain: [number, number]): void {
    // Not used by pie; kept for API parity
    this.y_domain = domain;
  }

  public ResetYDomain(): void {
    this.y_domain = null;
  }

  public SetValueRanges(ranges: readonly ValueRange[]): void {
    this.valueRanges = (ranges ?? []) as ValueRange[];
  }

  public OnBrush(_callback: (x_domain: [Date, Date]) => void): void {
    // No-op for pie (parity)
  }

  public OnBrushReset(_callback: (x_domain: [Date, Date]) => void): void {
    // No-op for pie (parity)
  }

  public ResetBrush(): void {
    // No-op for pie
  }

  // ---------------- Rendering ----------------
  private renderGraph(_isMobile: boolean, _settings: UserSettings): void {
    d3.select(`svg#${this.id}`).remove();

    const element = this.chartContainer.nativeElement;
    const max_width = element.clientWidth;
    const max_height = window.innerWidth < 376 ? 220 : element.clientHeight;

    // Layout
    const margin = { top: 50, right: 0, bottom: 40, left: 0 };
    const width = Math.max(0, max_width - margin.left - margin.right);
    const height = Math.max(0, max_height - margin.top - margin.bottom);
    if (height <= 0 || width <= 0) return;

    const radius = Math.min(width, height) / 2;

    // Root svg & centered group
    const svgRoot = d3
      .select(element)
      .append('svg')
      .attr('id', this.id)
      .attr('width', max_width)
      .attr('height', max_height);

    const svg = svgRoot
      .append('g')
      .attr('transform', `translate(${width / 2 + margin.left}, ${height / 2 + margin.top})`);

    // Pie & arcs
    const pieGen = d3
      .pie<CategoryDatum>()
      .sort(null)
      .value((d) => d.value);

    const arcGen = d3
      .arc<d3.PieArcDatum<CategoryDatum>>()
      .innerRadius(radius * 0.55)
      .outerRadius(radius * 0.8);

    const outerArc = d3
      .arc<d3.PieArcDatum<CategoryDatum>>()
      .innerRadius(radius * 0.9)
      .outerRadius(radius * 0.9);

    const outerArc2 = d3
      .arc<d3.PieArcDatum<CategoryDatum>>()
      .innerRadius(radius * 1.15)
      .outerRadius(radius * 1.15);

    // Category colors
    const categoryColor: Record<CategoryKey, string> = {
      Working: '#00AA75',
      'Blow through': '#ECB102',
      Blocked: '#E1000F',
    };

    // Cached arc states for tweening
    const oldArcs = new Map<number, d3.PieArcDatum<CategoryDatum>>(); // slices
    const oldLbls = new Map<number, d3.PieArcDatum<CategoryDatum>>(); // labels transform
    const oldLblDx = new Map<number, d3.PieArcDatum<CategoryDatum>>(); // labels dx
    const oldLines = new Map<number, d3.PieArcDatum<CategoryDatum>>(); // leaders

    // Build initial fractions
    let { fractions, labels } = generateFractions(this.data, this.x_domain);

    // Initial pie data
    const dataArr: CategoryDatum[] = asArray(fractions);
    let pieData = pieGen(dataArr);

    // --- Initial draw ---

    // 1) Slices
    let sliceSel = svg
      .selectAll<SVGPathElement, d3.PieArcDatum<CategoryDatum>>('path.slice')
      .data(pieData, (d: any) => d.data.key);

    sliceSel
      .enter()
      .append('path')
      .attr('class', 'slice')
      .attr('d', arcGen)
      .attr('fill', (d) => categoryColor[d.data.key])
      .attr('stroke', (d) => categoryColor[d.data.key])
      .style('stroke-width', '2px');

    // 2) Leader lines
    let lineSel = svg
      .selectAll<SVGPolylineElement, d3.PieArcDatum<CategoryDatum>>('polyline.leader')
      .data(pieData, (d: any) => d.data.key);

    lineSel
      .enter()
      .append('polyline')
      .attr('class', 'leader')
      .attr('stroke', 'black')
      .style('fill', 'none')
      .attr('stroke-width', 1)
      .attr('points', (d) => {
        const posA = outerArc.centroid(d);
        const posB = outerArc2.centroid(d);
        const posC = outerArc2.centroid(d);
        const mid = midAngle(d);
        posC[0] = radius * 1.1 * (mid < Math.PI ? 1 : -1);
        return `${posA[0]},${posA[1]} ${posB[0]},${posB[1]} ${posC[0]},${posC[1]}`;
      });

    // 3) Labels
    let labelSel = svg
      .selectAll<SVGTextElement, d3.PieArcDatum<CategoryDatum>>('text.label')
      .data(pieData, (d: any) => d.data.key);

    labelSel
      .enter()
      .append('text')
      .attr('class', 'label')
      .text((d) => labels[d.data.key])
      .attr('transform', (d) => {
        const pos = outerArc2.centroid(d);
        const mid = midAngle(d);
        pos[0] = radius * 1.2 * (mid < Math.PI ? 1 : -1);
        pos[1] += 5;
        return `translate(${pos})`;
      })
      .attr('dx', function (this: SVGTextElement, d) {
        const mid = midAngle(d);
        return mid < Math.PI ? '0' : `${-this.getBBox().width}px`;
      });

    // ---------- Update pipeline ----------
    this.updateChart = () => {
      // Recompute fractions from (possibly) new domain
      const f = generateFractions(this.data, this.x_domain);
      fractions = f.fractions;
      labels = f.labels;

      const nextArr: CategoryDatum[] = asArray(fractions);
      pieData = pieGen(nextArr);

      // Label overlap offsets (same rule as your legacy)
      const { labelOffsetsRad } = computeLabelOffsets(pieData, outerArc2, radius);

      // Slices
      sliceSel = svg
        .selectAll<SVGPathElement, d3.PieArcDatum<CategoryDatum>>('path.slice')
        .data(pieData, (d: any) => d.data.key);

      sliceSel
        .interrupt()
        .transition()
        .duration(500)
        .attrTween('d', (d) => arcTweenPath(d, arcGen, oldArcs))
        .style('stroke-width', (d) => (d.startAngle === d.endAngle ? '0px' : '2px'))
        .selection();

      // Labels
      labelSel = svg
        .selectAll<SVGTextElement, d3.PieArcDatum<CategoryDatum>>('text.label')
        .data(pieData, (d: any) => d.data.key);

      labelSel
        .text((d) => labels[d.data.key])
        .interrupt()
        .transition()
        .duration(500)
        .attrTween('transform', (d) =>
          arcTweenTransform(d, outerArc2, radius, labelOffsetsRad, oldLbls)
        )
        .attrTween('dx', function (this: SVGTextElement, d) {
          return arcTweenDx(d, this, labelOffsetsRad, oldLblDx);
        })
        .style('opacity', (d) => (d.startAngle === d.endAngle ? 0 : 1))
        .selection();

      // Leader lines
      lineSel = svg
        .selectAll<SVGPolylineElement, d3.PieArcDatum<CategoryDatum>>('polyline.leader')
        .data(pieData, (d: any) => d.data.key);

      lineSel
        .interrupt()
        .transition()
        .duration(500)
        .attrTween('points', (d) =>
          arcTweenLeader(d, outerArc, outerArc2, radius, labelOffsetsRad, oldLines)
        )
        .style('opacity', (d) => (d.startAngle === d.endAngle ? 0 : 1))
        .selection();
    };

    // Initial draw complete → kick the first update (legacy used timeout)
    setTimeout(() => this.updateChart?.(), 250);
  }
}

// ---------------- Utilities ----------------
function midAngle(d: d3.PieArcDatum<CategoryDatum>): number {
  return d.startAngle + (d.endAngle - d.startAngle) / 2;
}

function asArray(fr: Record<CategoryKey, number>): CategoryDatum[] {
  // Keep insertion order: Working, Blow through, Blocked
  return (Object.keys(fr) as CategoryKey[]).map((k) => ({ key: k, value: fr[k] || 0 }));
}

/** Compute pie fractions with time-weighting and boundary insertion (legacy parity). */
function generateFractions(
  all: Point[],
  domain: [Date, Date] | null
): { fractions: Record<CategoryKey, number>; labels: Record<CategoryKey, string> } {
  const filtered = filterWithBoundary(all, domain);

  const acc = [0, 0, 0]; // [Working, Blow through, Blocked]
  let lastTs: Date | null = null;

  for (const item of filtered) {
    const idx = bucketIndex(item.value);
    const diff = lastTs ? +item.date - +lastTs : 0;
    acc[idx] += Math.max(0, diff);
    lastTs = item.date;
  }

  const sum = acc[0] + acc[1] + acc[2];
  const f: Record<CategoryKey, number> = {
    Working: sum > 0 ? acc[0] / sum : 0,
    'Blow through': sum > 0 ? acc[1] / sum : 0,
    Blocked: sum > 0 ? acc[2] / sum : 0,
  };

  const lbl: Record<CategoryKey, string> = {
    Working: (f['Working'] * 100).toFixed(0) + '%',
    'Blow through': (f['Blow through'] * 100).toFixed(0) + '%',
    Blocked: (f['Blocked'] * 100).toFixed(0) + '%',
  };

  return { fractions: f, labels: lbl };

  function bucketIndex(val: number): 0 | 1 | 2 {
    if (val <= 15) return 0; // Working
    if (val <= 65) return 1; // Blow through
    return 2; // Blocked
  }
}

/** Filter, and inject synthetic point at domain start with last prior value (legacy parity). */
function filterWithBoundary(all: Point[], domain: [Date, Date] | null): Point[] {
  if (!domain) return [...all].sort((a, b) => +a.date - +b.date);

  const [start, end] = domain;
  const inside = all
    .filter((p) => p.date >= start && p.date <= end)
    .sort((a, b) => +a.date - +b.date);

  const before = all.filter((p) => p.date < start).sort((a, b) => +a.date - +b.date);

  // If there was any data before start that's excluded, inject the last one at start.
  if (inside.length < all.filter((p) => p.date <= end).length) {
    const prev = before[before.length - 1];
    if (prev) inside.unshift({ ...prev, date: new Date(start) });
  }
  return inside;
}

/** Label overlap avoidance (keeps your legacy min-gap by offsetting the middle slice only). */
function computeLabelOffsets(
  arcs: d3.PieArcDatum<CategoryDatum>[],
  labArc: d3.Arc<any, d3.PieArcDatum<CategoryDatum>>,
  r: number
): { labelOffsetsRad: number[] } {
  const minLabelDiff = 30; // px
  const posY = arcs.map((d) => labArc.centroid(d)[1] + r * 1.15);
  const sides = arcs.map((d) => midAngle(d) < Math.PI);

  // Order by your data array: Working, Blow through, Blocked
  const offsets: number[] = [0, 0, 0];

  // 0 ↔ 1 overlap on same side
  if (Math.abs(posY[1] - posY[0]) < minLabelDiff && sides[1] === sides[0]) {
    const adjustPx = minLabelDiff - Math.abs(posY[1] - posY[0]);
    offsets[1] = Math.asin(adjustPx / (r * 1.2)) * 2;
  }
  // 1 ↔ 2 overlap on same side
  else if (Math.abs(posY[1] - posY[2]) < minLabelDiff && sides[1] === sides[2]) {
    const adjustPx = minLabelDiff - Math.abs(posY[1] - posY[2]);
    offsets[1] = -Math.asin(adjustPx / (r * 1.2)) * 2;
  }
  return { labelOffsetsRad: offsets };
}

// --------- Tweens with cached previous states (smooth transitions) ---------
function arcTweenPath(
  d: d3.PieArcDatum<CategoryDatum>,
  gen: d3.Arc<any, d3.PieArcDatum<CategoryDatum>>,
  cache: Map<number, d3.PieArcDatum<CategoryDatum>>
) {
  const prev = cache.get(d.index) ?? { ...d, startAngle: d.startAngle, endAngle: d.startAngle };
  const i = d3.interpolate(prev, d);
  cache.set(d.index, d);
  return (t: number) => gen(i(t))!;
}

function arcTweenTransform(
  d: d3.PieArcDatum<CategoryDatum>,
  labArc: d3.Arc<any, d3.PieArcDatum<CategoryDatum>>,
  r: number,
  offsets: number[],
  cache: Map<number, d3.PieArcDatum<CategoryDatum>>
) {
  const adj = { ...d };
  const prev = cache.get(d.index) ?? {
    ...adj,
    startAngle: adj.startAngle,
    endAngle: adj.startAngle,
  };
  const i = d3.interpolate(prev, adj);
  cache.set(d.index, d);

  return (t: number) => {
    const it = i(t) as d3.PieArcDatum<CategoryDatum>;
    const off = offsets[it.index] || 0;
    const mid = midAngle(it) + off;
    const pos = labArc.centroid(it);
    pos[0] = r * 1.2 * (mid < Math.PI ? 1 : -1);
    pos[1] += 5;
    return `translate(${pos})`;
  };
}

function arcTweenDx(
  d: d3.PieArcDatum<CategoryDatum>,
  node: SVGTextElement,
  offsets: number[],
  cache: Map<number, d3.PieArcDatum<CategoryDatum>>
) {
  const prev = cache.get(d.index) ?? { ...d, startAngle: d.startAngle, endAngle: d.startAngle };
  const i = d3.interpolate(prev, d);
  cache.set(d.index, d);
  return (t: number) => {
    const it = i(t) as d3.PieArcDatum<CategoryDatum>;
    const mid = midAngle(it) + (offsets[it.index] || 0);
    if (mid < Math.PI) return '0';
    const w = node.getBBox?.().width ?? 0;
    return `${-w}px`;
  };
}

function arcTweenLeader(
  d: d3.PieArcDatum<CategoryDatum>,
  a1: d3.Arc<any, d3.PieArcDatum<CategoryDatum>>,
  a2: d3.Arc<any, d3.PieArcDatum<CategoryDatum>>,
  r: number,
  offsets: number[],
  cache: Map<number, d3.PieArcDatum<CategoryDatum>>
) {
  const prev = cache.get(d.index) ?? { ...d, startAngle: d.startAngle, endAngle: d.startAngle };
  const i = d3.interpolate(prev, d);
  cache.set(d.index, d);
  return (t: number) => {
    const it = i(t) as d3.PieArcDatum<CategoryDatum>;
    const off = offsets[it.index] || 0;
    const posA = a1.centroid(it);
    const adj = { ...it, startAngle: it.startAngle + off, endAngle: it.endAngle + off } as any;
    const posB = a2.centroid(adj);
    const posC = a2.centroid(adj);
    const mid = midAngle(it) + off;
    posC[0] = r * 1.1 * (mid < Math.PI ? 1 : -1);
    return `${posA[0]},${posA[1]} ${posB[0]},${posB[1]} ${posC[0]},${posC[1]}`;
  };
}
